import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
dayjs.extend(timezone);

/** Fuso orario delle sedi fisiche (Milano, Torino). */
export const SEDE_TIMEZONE = 'Europe/Rome';


export default class DateUtil {

    static getMomentDate(date?: Date): Dayjs {

        if (date)
            return dayjs(date);
        else
            return dayjs();

    }

    static addDays(currentDate: Dayjs, dayToAdd: number): Dayjs {

        return currentDate.add(dayToAdd, 'day');

    }

    static subtractDays(currentDate: Dayjs, dayToSubtract: number): Dayjs {

        return currentDate.subtract(dayToSubtract, 'day');

    }

    static subtractMounth(currentDate: Date, months: number): Date {

        return dayjs(currentDate).subtract(months, 'month').toDate();

    }

    static getFirstDayOfTheWeek(currentDate: Dayjs): Dayjs {
        console.log('weekday => ' + currentDate.day())
        switch (currentDate.day()) {
            case 2: {
                currentDate = currentDate.subtract(1, 'day');
            }
                break;
            case 3: {
                currentDate = currentDate.subtract(2, 'day');
            }
                break;
            case 4: {
                currentDate = currentDate.subtract(3, 'day');
            }
                break;
            case 5: {
                currentDate = currentDate.subtract(4, 'day');
            }
                break;
            case 6: {
                currentDate = currentDate.subtract(5, 'day');
            }
                break;
            case 0: {
                currentDate = currentDate.subtract(6, 'day');
            }
                break;
        }
        return currentDate;
    }

    static compareDate(date1: Date, date2: Date): boolean {
        const time1 = dayjs(date1).format('YYYY-MM-DD');
        const time2 = dayjs(date2).format('YYYY-MM-DD');

        if (time1 == time2)
            return true;
        else
            return false;
    }

    static getDateStringNoTime(dataToReturn: Date): string {
        return dayjs(dataToReturn).format('YYYY-MM-DD').toString();

    }

    static getDateStringNoTimeWithFormat(dataToReturn: Date, format: DateFormat): string {
        return dayjs(dataToReturn).format(format.toString()).toString();

    }

    static subtractYearFromDate(data: Date, year: number): Date {
        return dayjs(data).subtract(year, 'year').toDate();
    }

    static subtractYearFromDateFormatted(data: Date, format: string, year: number): string {
        return dayjs(this.subtractYearFromDate(data, year)).format(format).toString();
    }

    static isInDateRange(dateToCompare: Date, dateStart: Date, dateEnd: Date, granularity?: dayjs.OpUnitType, inclusivity?: "()" | "[)" | "(]" | "[]"): boolean {
        const dateToCompareOnlyDate = dayjs(dateToCompare).format('YYYY-MM-DD');
        const dateStartOnlyDate = dayjs(dateStart).format('YYYY-MM-DD');
        const dateEndOnlyDate = dayjs(dateEnd).format('YYYY-MM-DD');
        return dayjs(dateToCompareOnlyDate).isBetween(dateStartOnlyDate, dateEndOnlyDate, granularity as any, inclusivity);
    }

    static isInDateRangeMoment(dateToCompare: Dayjs, dateStart: Dayjs, dateEnd: Dayjs, granularity?: dayjs.OpUnitType, inclusivity?: "()" | "[)" | "(]" | "[]"): boolean {
        const dateToCompareOnlyDate = dayjs(dateToCompare.format('YYYY-MM-DD'));
        const dateStartOnlyDate = dayjs(dateStart.format('YYYY-MM-DD'));
        const dateEndOnlyDate = dayjs(dateEnd.format('YYYY-MM-DD'));
        return dateToCompareOnlyDate.isBetween(dateStartOnlyDate, dateEndOnlyDate, granularity as any, inclusivity);
    }

    /**
     * Data/ora ISO 8601 per il giorno indicato all'orario `time` (HH:mm:ss) nel fuso delle sedi,
     * con l'offset valido in quel giorno: +02:00 con l'ora legale, +01:00 con l'ora solare.
     */
    static toSedeDateTime(day: Date, time: string): string {
        return dayjs.tz(`${this.getDateStringNoTime(day)} ${time}`, SEDE_TIMEZONE).format('YYYY-MM-DDTHH:mm:ssZ');
    }

    /** True se gli intervalli [start1, end1) e [start2, end2) hanno almeno un istante in comune. */
    static isTimeRangeOverlapping(start1: Date | string, end1: Date | string, start2: Date | string, end2: Date | string): boolean {
        if (!start1 || !end1 || !start2 || !end2) return false;
        return dayjs(start1).isBefore(dayjs(end2)) && dayjs(end1).isAfter(dayjs(start2));
    }

    static decodeDate(date: string): Date {
        return dayjs(date).toDate();
    }

    static isDateBeforeToday(date: Date): boolean {
        let isBeForeToday: boolean;
        let today = dayjs();
        isBeForeToday = dayjs(date).isBefore(today, "day");
        return isBeForeToday;
    }

    static isDateBeforeTargetDate(dateToCheck: Date, dateTarget: Date): boolean {
        return dayjs(dateToCheck).isBefore(dateTarget, "day");
    }

    static isDateSameOrAfterToday(date: Date): boolean {
        let isBeForeToday: boolean;
        let today = dayjs();
        isBeForeToday = dayjs(date).isSameOrAfter(today, "day");
        return isBeForeToday;
    }

    static decodeDateTimeString(date: Date): string {
        return dayjs(date).format('HH:mm');
    }

    static decodeItaFullDateString(date: Date): string {
        let giorno = this.decodeDayItaFullString(date.getDay());
        let mese = this.decodeMounthItaString(date.getMonth());

        return `${giorno} ${date.getDate()} ${mese}`
    }

    static decodeItaExtendedDateString(date: Date): string {
        const dateDayjs = this.getMomentDate(date);
        const mese = this.decodeMounthItaString(dateDayjs.month());
        return `${dateDayjs.date()} ${mese} ${dateDayjs.year()}`;
    }

    static decodeDayItaFullString(day: number): string {
        switch (day) {
            case 0:
                return 'Domenica';
            case 1:
                return 'Lunedì';
            case 2:
                return 'Martedì';
            case 3:
                return 'Mercoledì';
            case 4:
                return 'Giovedì';
            case 5:
                return 'Venerdì';
            case 6:
                return 'Sabato';
        }
    }

    static decodeDayItaString(day: number): string {
        switch (day) {
            case 0:
                return 'DOM';
            case 1:
                return 'LUN';
            case 2:
                return 'MAR';
            case 3:
                return 'MER';
            case 4:
                return 'GIO';
            case 5:
                return 'VEN';
            case 6:
                return 'SAB';
        }
    }

    static formatDateOnlyDate(date: Date): string {
        return dayjs(date).format("DD-MM-YYYY");
    }

    static decodeMounthItaString(mounth: number): string {
        switch (mounth) {
            case 0:
                return 'Gennaio';
            case 1:
                return 'Febbraio';
            case 2:
                return 'Marzo';
            case 3:
                return 'Aprile';
            case 4:
                return 'Maggio';
            case 5:
                return 'Giugno';
            case 6:
                return 'Luglio';
            case 7:
                return 'Agosto';
            case 8:
                return 'Settembre';
            case 9:
                return 'Ottobre';
            case 10:
                return 'Novembre';
            case 11:
                return 'Dicembre';

        }
    }

    static getHolidayName(date: Date): string | null {
        const d = dayjs(date);
        const day = d.date();
        const month = d.month(); // 0-indexed

        if (day === 1 && month === 0) return 'Capodanno';
        if (day === 6 && month === 0) return 'Epifania';
        if (day === 25 && month === 3) return 'Liberazione';
        if (day === 1 && month === 4) return 'Festa del Lavoro';
        if (day === 2 && month === 5) return 'Festa della Repubblica';
        if (day === 15 && month === 7) return 'Ferragosto';
        if (day === 1 && month === 10) return 'Ognissanti';
        if (day === 8 && month === 11) return 'Immacolata';
        if (day === 25 && month === 11) return 'Natale';
        if (day === 26 && month === 11) return 'S. Stefano';

        const pasqua = DateUtil.getEasterSunday(d.year());
        if (d.isSame(pasqua, 'day')) return 'Pasqua';
        if (d.isSame(pasqua.add(1, 'day'), 'day')) return 'Pasquetta';

        return null;
    }

    /** Domenica di Pasqua (algoritmo di Meeus/Jones/Butcher). */
    static getEasterSunday(year: number): Dayjs {
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;
        return dayjs(new Date(year, month - 1, day));
    }

    /** Lunedì-venerdì non festivo. */
    static isWorkingDay(date: Date | string | Dayjs): boolean {
        const d = dayjs(date);
        const dow = d.day();
        return dow >= 1 && dow <= 5 && !DateUtil.getHolidayName(d.toDate());
    }

    static nextWorkingDay(date: Date | string | Dayjs): Dayjs {
        let d = dayjs(date).startOf('day').add(1, 'day');
        while (!DateUtil.isWorkingDay(d)) d = d.add(1, 'day');
        return d;
    }

    /**
     * La presenza di un giorno si conferma dal giorno stesso fino a fine del giorno lavorativo successivo
     * (stessa regola dell'API); dopo può sanarla solo HR/HC.
     */
    static isWithinConfirmationWindow(bookingDate: Date | string, today: Date | Dayjs = dayjs()): boolean {
        const giorno = dayjs(bookingDate).startOf('day');
        const oggi = dayjs(today).startOf('day');
        return !giorno.isAfter(oggi) && !oggi.isAfter(DateUtil.nextWorkingDay(giorno));
    }

    /**
     * Verifica se una data è bloccata per chiusura consuntivo stipendi / presenze.
     * Default cutoff day: 8 del mese corrente.
     * - Mese corrente e futuro: NON bloccato.
     * - Mese precedente: bloccato se oggi > cutoffDay.
     * - Mesi antecedenti al mese scorso (>= 2 mesi fa): sempre bloccato.
     */
    static isDatePayrollLocked(targetDate: Date | string, cutoffDay: number = 8): boolean {
        if (!targetDate) return false;
        const now = dayjs();
        const target = dayjs(targetDate);
        if (!target.isValid()) return false;

        const currentMonthStart = now.startOf('month');
        if (!target.isBefore(currentMonthStart, 'day')) {
            return false;
        }

        const prevMonthStart = now.subtract(1, 'month').startOf('month');
        if (target.isBefore(prevMonthStart, 'day')) {
            return true;
        }

        return now.date() > cutoffDay;
    }

}

export enum DateFormat {
    "YYYY-MM-DD" = "YYYY-MM-DD",
    "YYYY-DD-MM" = "YYYY-DD-MM"
}